import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Checkbox,
  FormControlLabel,
  Box,
  Typography,
  Alert,
  Divider,
  FormGroup,
  CircularProgress,
  Chip
} from '@mui/material';
import { Warning as WarningIcon } from '@mui/icons-material';
import api, { TenantResourceCounts } from '../services/api';

interface TenantDeleteDialogProps {
  open: boolean;
  tenantId: string;
  tenantName: string;
  onClose: () => void;
  onDelete: () => void;
}


interface ResourceType {
  id: string;
  label: string;
  description: string;
  // Deleting this leaves this environment's database and acts on the shared cloud
  // account. Outside production the identifiers involved are production's -- the
  // database is a clone -- so the API lets these be declined there.
  sharedWithProduction?: boolean;
}

const RESOURCE_TYPES: ResourceType[] = [
  { id: 'bots', label: 'Bots', description: 'Delete all bot records (required)' },
  { id: 'bot_instances', label: 'Cloud Instances', description: 'Terminate the EC2 instances those bot records point at', sharedWithProduction: true },
  { id: 'scenarios', label: 'Scenarios', description: 'Delete all scenario configurations (required)' },
  { id: 'executions', label: 'Executions', description: 'Delete execution history (required)' },
  { id: 'allow_list', label: 'Allow List Entries', description: 'Delete all allow list entries and their asset discovery data (required)' },
  { id: 'providers', label: 'Private Providers', description: 'Delete tenant-specific cloud providers (required)' },
  { id: 'user_permissions', label: 'User-Tenant Permissions', description: 'Remove all user access to tenant' },
  { id: 's3_bucket', label: 'S3 Bucket', description: 'Delete S3 bucket and all objects', sharedWithProduction: true },
  { id: 'iam_policy', label: 'IAM Policy', description: 'Delete tenant IAM policy', sharedWithProduction: true },
  { id: 'tenant_record', label: 'Tenant Record', description: 'Delete the tenant record itself (required)' }
];

// Used until the API answers, and when an older API omits `environment`: production's
// policy, where the tenant's rows and the instances they point at all go together.
// The live list comes from the API -- backend/environment_policy.py owns the rules.
const FALLBACK_MANDATORY_RESOURCES = [
  'tenant_record', 'bots', 'executions', 'scenarios', 'allow_list', 'providers', 'bot_instances'
];

const TenantDeleteDialog: React.FC<TenantDeleteDialogProps> = ({
  open,
  tenantId,
  tenantName,
  onClose,
  onDelete
}) => {
  const [loading, setLoading] = useState(false);
  const [loadingCounts, setLoadingCounts] = useState(false);
  const [resourceCounts, setResourceCounts] = useState<TenantResourceCounts | null>(null);
  const [selectedResources, setSelectedResources] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const environment = resourceCounts?.environment;
  const isProduction = environment?.is_production ?? true;
  const requiredResources = useMemo(
    () => new Set(environment?.mandatory_resources ?? FALLBACK_MANDATORY_RESOURCES),
    [environment]
  );
  const sharedResources = useMemo(
    () => new Set(environment?.shared_with_production ?? []),
    [environment]
  );

  // Everything is ticked by default, except that outside production the resources
  // shared with production start off -- deleting them here would delete production's.
  const defaultSelection = useCallback((counts: TenantResourceCounts): Set<string> => {
    const shared = new Set(counts.environment?.shared_with_production ?? []);
    const isProd = counts.environment?.is_production ?? true;
    return new Set(
      RESOURCE_TYPES.map(r => r.id).filter(id => isProd || !shared.has(id))
    );
  }, []);

  const fetchResourceCounts = useCallback(async () => {
    setLoadingCounts(true);
    setError(null);
    try {
      const counts = await api.getTenantResourceCounts(tenantId);
      setResourceCounts(counts);
      // The defaults depend on the environment the API reports, so they are set here
      // rather than when the dialog opens.
      setSelectedResources(defaultSelection(counts));
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to fetch resource counts');
      console.error('Error fetching resource counts:', err);
    } finally {
      setLoadingCounts(false);
    }
  }, [tenantId, defaultSelection]);

  useEffect(() => {
    if (open && tenantId) {
      setSelectedResources(new Set());
      fetchResourceCounts();
    }
  }, [open, tenantId, fetchResourceCounts]);

  const handleToggleResource = (resourceId: string) => {
    // Resources this environment treats as mandatory cannot be deselected
    if (requiredResources.has(resourceId)) {
      return;
    }
    
    const newSelected = new Set(selectedResources);
    if (newSelected.has(resourceId)) {
      newSelected.delete(resourceId);
    } else {
      newSelected.add(resourceId);
    }
    setSelectedResources(newSelected);
  };

  const handleSelectAll = () => {
    setSelectedResources(new Set(RESOURCE_TYPES.map(r => r.id)));
  };

  const handleDeselectAll = () => {
    // Always keep the resources this environment treats as mandatory
    setSelectedResources(new Set(requiredResources));
  };

  const getResourceCount = (resourceId: string): number => {
    if (!resourceCounts) return 0;
    
    const countMap: Record<string, keyof TenantResourceCounts> = {
      bots: 'bots',
      bot_instances: 'bot_instances',
      scenarios: 'scenarios',
      executions: 'executions',
      allow_list: 'allow_list',
      providers: 'providers',
      user_permissions: 'user_permissions'
    };
    
    const key = countMap[resourceId];
    return key ? resourceCounts[key] as number : 0;
  };

  const handleDelete = async () => {
    setDeleteError(null);
    setLoading(true);
    
    try {
      const resourcesToDelete = Array.from(selectedResources);
      await api.deleteTenant(tenantId, resourcesToDelete);
      onDelete();
      onClose();
    } catch (err: any) {
      setDeleteError(err.response?.data?.detail || 'Failed to delete tenant');
      console.error('Error deleting tenant:', err);
    } finally {
      setLoading(false);
    }
  };

  const canDelete = resourceCounts?.validation.can_delete ?? false;
  const validationErrors: string[] = [];
  
  if (resourceCounts) {
    if (resourceCounts.validation.has_running_executions) {
      validationErrors.push(
        `${resourceCounts.validation.running_executions_count} running execution(s) found. Please stop all running attacks first.`
      );
    }
    if (resourceCounts.validation.has_active_scenarios) {
      validationErrors.push(
        `${resourceCounts.validation.active_scenarios_count} active scenario(s) found. Please deactivate all scenarios first.`
      );
    }
    if (resourceCounts.validation.s3_scenario_blocks_delete ?? resourceCounts.validation.has_s3_scenario) {
      validationErrors.push('An attack scenario is currently set in S3. Please end the attack first.');
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <WarningIcon color="error" />
          Delete Tenant: {tenantName}
        </Box>
      </DialogTitle>
      <DialogContent>
        {loadingCounts ? (
          <Box display="flex" justifyContent="center" py={3}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : (
          <>
            {!canDelete && (
              <Alert severity="error" sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Cannot delete tenant. Please resolve the following issues:
                </Typography>
                <ul style={{ margin: 0, paddingLeft: 20 }}>
                  {validationErrors.map((msg, idx) => (
                    <li key={idx}>{msg}</li>
                  ))}
                </ul>
              </Alert>
            )}
            
            {!isProduction && (
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  {environment?.name ?? 'Non-production'} environment
                </Typography>
                This database is a clone of production on the same cloud account, so the resources
                marked <strong>shared with production</strong> are production&apos;s: ticking one here
                terminates production&apos;s instances or empties production&apos;s bucket. They are left
                unchecked by default &mdash; the tenant&apos;s rows are deleted either way.
              </Alert>
            )}

            <Alert severity="warning" sx={{ mb: 2 }}>
              This action cannot be undone. Select the resources you want to delete:
            </Alert>

            <FormGroup>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Button size="small" onClick={handleSelectAll}>
                  Select All
                </Button>
                <Button size="small" onClick={handleDeselectAll}>
                  Deselect All
                </Button>
              </Box>
              <Divider sx={{ my: 1 }} />
              
              {RESOURCE_TYPES.map((resource) => {
                const count = getResourceCount(resource.id);
                const isSelected = selectedResources.has(resource.id);
                const isRequired = requiredResources.has(resource.id);
                // Only worth flagging where it is somebody else's resource.
                const isShared = !isProduction && sharedResources.has(resource.id);
                
                return (
                  <FormControlLabel
                    key={resource.id}
                    control={
                      <Checkbox
                        checked={isSelected}
                        disabled={isRequired || !canDelete}
                        onChange={() => handleToggleResource(resource.id)}
                      />
                    }
                    label={
                      <Box>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography variant="body1">
                            {resource.label}
                            {count > 0 && ` (${count})`}
                          </Typography>
                          {isShared && (
                            <Chip label="shared with production" color="warning" size="small" variant="outlined" />
                          )}
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          {resource.description}
                        </Typography>
                      </Box>
                    }
                    sx={{ mb: 1 }}
                  />
                );
              })}
            </FormGroup>

            {deleteError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {deleteError}
              </Alert>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleDelete}
          color="error"
          variant="contained"
          disabled={loading || loadingCounts || !canDelete}
        >
          {loading ? <CircularProgress size={20} /> : 'Delete Tenant'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TenantDeleteDialog;

